/* eslint-disable no-script-url,jsx-a11y/anchor-is-valid,no-undef */
import { Button, FormControlLabel, Switch, Tooltip } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import {
  PhoneNumberFormat as PNF,
  PhoneNumberUtil,
} from 'google-libphonenumber';
import moment from 'moment';
import { useEffect, useMemo, useRef, useState } from 'react';
import SVG from 'react-inlinesvg';
import { Link, useHistory } from 'react-router-dom';
import {
  useRecoilState,
  useRecoilValueLoadable,
  useSetRecoilState,
} from 'recoil';
import { SwitchBusinessModal } from '../../../../../app/components/modals/SwitchBusinessModal';
import { useSnackbarWithStyles } from '../../../../../app/components/notifications/SnackbarProviderWithStyles';
import { showUpdateCard } from '../../../../../app/components/stripe/showUpdateCard';
import { resetBusinessID } from '../../../../../common/changeBusinessID';
import { useMakePHPRequest } from '../../../../../common/useMakePHPRequest';
import {
  themeModeAtom,
  themeModeEnum,
} from '../../../../../recoil/atoms/themeModeAtom';
import { user } from '../../../../../recoil/atoms/user';
import { permissionsSelector } from '../../../../../recoil/selectors/permissionsSelector';
import { logoutAjax } from '../../../../../services/auth/logoutAjax';
import { toAbsoluteUrl } from '../../../../_helpers';
// import Notes from '../../../../../app/components/notes/Notes';
import { useDispatch, useSelector } from 'react-redux';
import { toggleSuperAdminDrawer } from '../../../../../app/store/superAdminStatsSlice';
import {
  mixpanelReset,
  trackLogOut,
} from '../../../../../common/userTracking.ts';

const businessSnapshotEnabled = process.env.VITE_APP_BUSINESS_SNAPSHOT === '1';

const hasCreditCardPrivilege = (role) => {
  return ['superadmin', 'admin', 'manager'].includes(
    String(role).toLowerCase()
  );
};

/**
 * Format number using google-libphonenumber
 * @see https://github.com/ruimarinho/google-libphonenumber
 *
 * @param {string} number Number to format in US
 * @returns {string} Formatted number with + and dashes
 */
export const formatNumberUS = (number) => {
  const phoneUtil = PhoneNumberUtil.getInstance();
  try {
    return (
      number && // bypass if empty
      phoneUtil.format(
        phoneUtil.parseAndKeepRawInput(number, 'US'),
        PNF.INTERNATIONAL
      )
    );
  } catch {
    return null;
  }
};

const useStyles = makeStyles((theme) => ({
  contrastText: {
    color: `${theme.palette.getContrastText(
      theme.palette.background.paper
    )} !important`, // override bootstrap !important
  },
  profile: {
    width: '100%', // show full phone number
  },
  signout: {
    width: 130, // cap button at original size
  },
  button: {
    cursor: 'pointer',
  },
  section: {
    overflowX: 'auto',
  },
  naviText: {
    display: 'inline-block',
  },
  naviLink: {
    display: 'flex',
    padding: '.5rem 1.5rem',
  },
  symbolLabel: {
    backgroundColor: '#fff !important',
    border: '1px solid #ddd',
    '&.symbol-label svg': {
      height: '2rem !important',
      width: '2rem !important',
    },
  },
  internalOnly: {
    '& .menu-item': {
      display: 'inline-block',
      width: '25% !important',
    },
    '& .external': {
      display: 'none',
    },
    '& .navi-text': {
      display: 'none',
    },
  },
  statListItem: {
    listStyleType: 'none',
    padding: '0px',
    margin: 0,
  },
  barSkeleton: {
    height: 400,
    backgroundColor: theme.palette.mode === 'dark' ? 'dimgray' : 'gainsboro',
  },
  noDataContainer: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    minHeight: '400px',
  },
}));

export function QuickUser() {
  const classes = useStyles();
  const history = useHistory();
  const superAdminStats = useSelector((state) => state.superAdminStats);
  const dispatch = useDispatch();
  const setUserAtom = useSetRecoilState(user);
  const closeRef = useRef(null);
  const [switchBusinessModal, handleSwitchBusinessModal] = useState(false);
  const [originalBusinessObject, setObi] = useState(
    localStorage.getItem('originalBusinessObject')
  );
  const userAtom = useRecoilValueLoadable(user);
  const loginUser = userAtom.contents?.user ?? {};
  const fullName = `${loginUser.fname} ${loginUser.lname}`;
  const formattedNumber = useMemo(
    () => formatNumberUS(loginUser.callerid),
    [userAtom] // only format once per userAtom change
  );
  const { enqueueSnackbarWithStyles } = useSnackbarWithStyles();
  const businessSnapshotEnabled =
    process.env.VITE_APP_BUSINESS_SNAPSHOT === '1';
  // use requester
  const phpRequest = useMakePHPRequest({
    userAtom: userAtom.contents,
  });

  const handleLogout = async () => {
    closeRef.current.click(); // close modal
    mixpanelReset();
    localStorage.clear(); //clear local storage
    logoutAjax({ setUserAtom }); // logout by pass no params
  };

  const [themeMode, setThemeMode] = useRecoilState(themeModeAtom);
  const onChangeForSwitch = (event) => {
    setThemeMode(
      event.target.checked ? themeModeEnum.DARK_MODE : themeModeEnum.LIGHT_MODE
    );
    //set themeMode cookie
    localStorage.setItem(
      'themeMode',
      event.target.checked ? themeModeEnum.DARK_MODE : themeModeEnum.LIGHT_MODE
    );
  };
  const toggleDarkMode = () => {
    document.getElementById('darkmodeToggle').click();
  };
  const permissionsLoadable = useRecoilValueLoadable(permissionsSelector);
  const {
    canViewDebugInformation,
    shouldPopSuperAdminStats,
  } = permissionsLoadable.contents;

  const handleSwitchModal = () => {
    closeRef.current.click();
    handleSwitchBusinessModal(false);
    shouldPopSuperAdminStats &&
      !superAdminStats?.show &&
      dispatch(toggleSuperAdminDrawer(true));
    history.push(
      businessSnapshotEnabled
        ? '/call-reporting/business-snapshot'
        : '/call-reporting/agent-summary'
    );
  };

  const handleSwitch = () => {
    const res = resetBusinessID();
    if (res.success) {
      setUserAtom(res.user);
      setObi(null);
      handleSwitchModal();
    }
  };

  useEffect(() => {
    const updated = localStorage.getItem('originalBusinessObject');
    if (updated) {
      setObi(updated);
    } else {
      setObi(null);
    }
  }, [localStorage.getItem('originalBusinessObject'), setObi]);

  return (
    <div id="kt_quick_user" className="offcanvas offcanvas-right p-5">
      <div className="offcanvas-header d-flex align-items-center justify-content-between">
        <h3
          className={clsx(
            'font-weight-bold m-0 initialism text-muted',
            classes.contrastText
          )}
        >
          User Profile
          {/* <small className="text-muted font-size-sm ml-2">12 messages</small> */}
        </h3>
        <a
          ref={closeRef}
          href="#"
          className="btn btn-xs btn-icon btn-light btn-hover-primary"
          id="kt_quick_user_close"
        >
          <i className="ki ki-close icon-xs text-muted" />
        </a>
      </div>

      <div className="offcanvas-content pr-5 mr-n5">
        <div className="d-flex align-items-center mt-5">
          <div className="symbol symbol-25 mr-5 mt-n25">
            <Link
              to={`/my-profile/${loginUser.userid}/agent-reporting`}
              className="navi-item"
            >
              <div
                className={clsx('symbol-label', classes.symbolLabel)}
                style={{
                  backgroundImage: `url(${toAbsoluteUrl(
                    // '/media/svg/shapes/abstract-5.svg'
                    // '/media/svg/patterns/rhone.svg'
                    // '/media/svg/patterns/aare.svg'
                    // '/media/svg/patterns/taieri.svg'
                    // '/media/svg/patterns/rhone-2.svg'
                    '/media/users/blank.png'
                    // '/media/users/default.jpg'
                  )})`,
                }}
              />
              <i className="symbol-badge bg-success" />
            </Link>
          </div>
          <div className={`d-flex flex-column ${classes.profile}`}>
            <a
              href={`/my-profile/${loginUser.userid}/edit`}
              className={clsx(
                'font-weight-bold font-size-h5 text-dark-75 text-hover-primary',
                classes.contrastText
              )}
            >
              {fullName}
            </a>
            <div className="text-muted mt-1">{loginUser.role}</div>
            <div className="navi my-2">
              {/* <a href="#" className="navi-item"> */}
              {/* <span className="navi-link p-0 pb-2"> */}
              <span className="navi-icon mr-1">
                <span className="svg-icon-lg svg-icon-primary"></span>
              </span>
              <span className="navi-text text-muted text-hover-primary">
                {formattedNumber}
              </span>
              {/* </span> */}
              {/* </a> */}
            </div>
            <Button
              className={classes.signout}
              variant="contained"
              color="secondary"
              onClick={() => {
                trackLogOut();
                handleLogout();
              }}
              disableElevation
              data-pup="sign-out"
            >
              Sign out
            </Button>
          </div>
        </div>

        <div className="separator separator-dashed mt-6 mb-3" />

        <div
          className={clsx(
            'navi navi-spacer-x-0 p-0 header-menu menu-nav h-auto d-block',
            classes.navigation,
            { [classes.internalOnly]: loginUser.role === 'superadmin' }
          )}
        >
          <Link
            to={`/my-profile/${loginUser.userid}/agent-reporting`}
            className={clsx(
              'menu-item external',
              classes.button,
              classes.naviItem
            )}
          >
            <div className={clsx('navi-link', classes.naviLink)}>
              <div className="symbol symbol-40 mr-3">
                <Tooltip title="My Profile">
                  <div className={clsx('symbol-label', classes.symbolLabel)}>
                    <span className="svg-icon svg-icon-lg svg-icon-success">
                      <SVG
                        src={toAbsoluteUrl(
                          '/media/svg/icons/General/Notification2.svg'
                        )}
                      ></SVG>
                    </span>
                  </div>
                </Tooltip>
              </div>
              <div className="navi-text">
                <div className={clsx('font-weight-bold', classes.contrastText)}>
                  My Profile
                </div>
                <div className="text-muted">
                  Account settings and more
                  {/* <span className="label label-light-danger label-inline font-weight-bold">
                    update
                  </span> */}
                </div>
              </div>
            </div>
          </Link>
          <div
            data-pup="update-credit-card"
            className={clsx('menu-item', classes.button, classes.naviItem)}
            onClick={showUpdateCard(
              enqueueSnackbarWithStyles,
              phpRequest,
              userAtom.contents?.userid
            )}
            style={{
              display: hasCreditCardPrivilege(loginUser.role)
                ? 'inline-block'
                : 'none',
            }}
          >
            <div className={clsx('navi-link', classes.naviLink)}>
              <div className="symbol symbol-40 mr-3">
                <Tooltip title="Update Card">
                  <div className={clsx('symbol-label', classes.symbolLabel)}>
                    <span className="svg-icon svg-icon-lg svg-icon-warning">
                      <SVG
                        src={toAbsoluteUrl(
                          '/media/svg/icons/Shopping/Credit-card.svg'
                        )}
                      ></SVG>
                    </span>
                  </div>
                </Tooltip>
              </div>
              <div className={clsx('navi-text', classes.naviText)}>
                <div className={clsx('font-weight-bold', classes.contrastText)}>
                  Update Card
                </div>
                <div className="text-muted">Billing and Address</div>
              </div>
            </div>
          </div>

          <a
            href="https://kixie.zendesk.com/hc/en-us"
            className={clsx('menu-item', classes.naviItem)}
            target="_blank"
          >
            <div className={clsx('navi-link', classes.naviLink)}>
              <div className="symbol symbol-40 mr-3">
                <Tooltip title="Support">
                  <div className={clsx('symbol-label', classes.symbolLabel)}>
                    <span className="svg-icon svg-icon-lg svg-icon-danger">
                      <SVG
                        src={toAbsoluteUrl(
                          '/media/svg/icons/Files/Selected-file.svg'
                        )}
                      ></SVG>
                    </span>
                  </div>
                </Tooltip>
              </div>
              <div className={clsx('navi-text', classes.naviText)}>
                <div
                  className={clsx('font-weight-bold', classes.contrastText)}
                  // style={{ paddingTop: 8 }}
                >
                  Support
                </div>
                <div className="text-muted">Help and Guides</div>
              </div>
            </div>
          </a>

          <a
            className={clsx('menu-item', classes.naviItem)}
            onClick={toggleDarkMode}
          >
            <div className={clsx('navi-link', classes.naviLink)}>
              <div className="symbol symbol-40 mr-3">
                <Tooltip title="Dark Mode">
                  <div className={clsx('symbol-label', classes.symbolLabel)}>
                    <span className="svg-icon svg-icon-lg svg-icon-primary">
                      <SVG
                        src={toAbsoluteUrl('/media/svg/icons/Weather/Moon.svg')}
                      ></SVG>
                    </span>
                  </div>
                </Tooltip>
              </div>
              <div className={clsx('navi-text', classes.naviText)}>
                <FormControlLabel
                  style={{
                    marginLeft: -5,
                    marginRight: '0',
                    textAlign: 'start',
                  }}
                  control={
                    <Switch
                      name="theme-mode"
                      color="primary"
                      checked={themeMode === themeModeEnum.DARK_MODE}
                      onChange={onChangeForSwitch}
                      type="checkbox"
                    />
                  }
                  label="Dark Mode"
                  id="darkmodeToggle"
                />
              </div>
            </div>
          </a>

          {loginUser.role === 'superadmin' &&
            (!originalBusinessObject ? (
              <div
                className={clsx(
                  'menu-item navi-item',
                  classes.naviItem,
                  classes.button
                )}
                onClick={() => handleSwitchBusinessModal(true)}
              >
                <div className={clsx('navi-link px-5 pt-1', classes.naviLink)}>
                  <div className="symbol symbol-40 mr-3 superStyle">
                    <Tooltip title="Switch Business">
                      <div
                        className={clsx('symbol-label', classes.symbolLabel)}
                      >
                        <span className="svg-icon svg-icon-lg svg-icon-warning">
                          <SVG
                            src={toAbsoluteUrl(
                              '/media/svg/icons/Navigation/Exchange.svg'
                            )}
                          ></SVG>
                        </span>
                      </div>
                    </Tooltip>
                  </div>
                  <div className={clsx('navi-text', classes.naviText)}>
                    <div
                      className={clsx('font-weight-bold', classes.contrastText)}
                    >
                      Switch Business
                    </div>
                    <div className="text-muted">Change Business ID</div>
                  </div>
                </div>
              </div>
            ) : (
              <div
                className={clsx('menu-item', classes.button)}
                onClick={handleSwitch}
              >
                <div className={clsx('navi-link', classes.naviLink)}>
                  <div className="symbol symbol-40 bg-light-danger mr-3">
                    <div className={clsx('symbol-label', classes.symbolLabel)}>
                      <span className="svg-icon svg-icon-lg svg-icon-error">
                        X
                      </span>
                    </div>
                  </div>
                  <div className="navi-text">
                    <div
                      className={clsx('font-weight-bold', classes.contrastText)}
                    >
                      Viewing Business
                    </div>
                    <div className="text-muted">
                      {userAtom.contents?.businessid}
                    </div>
                  </div>
                </div>
              </div>
            ))}
        </div>
        {canViewDebugInformation && (
          <>
            <div className="separator separator-dashed mt-2 mb-7"></div>
            <article>
              <h6 className="mb-6">Release Information</h6>

              <section
                className={clsx(
                  'd-flex align-items-center bg-light-info rounded p-5',
                  classes.section
                )}
              >
                {/* <span className="svg-icon svg-icon-info mr-5">
                  <SVG
                    src={toAbsoluteUrl(
                      '/media/svg/icons/General/Attachment2.svg'
                    )}
                    className="svg-icon svg-icon-lg"
                  ></SVG>
                </span> */}
                <div className="d-flex flex-column flex-grow-1 mr-2">
                  <span className="text-muted font-size-sm">
                    build date:{' '}
                    {moment(window.kixieBuildEnv?.date).format(
                      'YYYY-MM-DD hh:mm:ss A'
                    )}
                  </span>
                  <div className="font-size-sm">
                    release:<b> {window.kixieBuildEnv?.release}</b>
                  </div>
                  <div className="font-size-sm">
                    env:<b> {window.kixieBuildEnv?.env}</b>
                  </div>
                  <div className="font-size-sm">
                    VITE_APP_ENV:<b> {process.env.VITE_APP_ENV}</b>
                  </div>
                  <div className="font-size-sm">
                    VITE_APP_COLOR:<b> {process.env.VITE_APP_COLOR}</b>
                  </div>
                  <a
                    href={`https://github.com/Kixie-com/Kixie-React/commit/${window.kixieBuildEnv.git}`}
                    className="font-weight-normel text-dark-75 text-hover-primary font-size-sm mb-1"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    git commit:<b> {window.kixieBuildEnv?.git}</b>
                  </a>
                  <div className="font-size-sm">
                    branch:<b> {window.kixieBuildEnv?.gitBranch}</b>
                  </div>
                  <div className="font-size-sm">
                    author:<b> {window.kixieBuildEnv?.gitInfo?.author}</b>
                  </div>
                  <div className="font-size-sm">
                    msg:
                    <b>
                      {window.kixieBuildEnv?.gitInfo?.msg
                        ?.replace(/\r?\n|\r/gm, ' ')
                        .substring(0, 80)}{' '}
                      ...
                    </b>
                  </div>
                  <div className="font-size-sm">
                    commit date:
                    <b>
                      {' '}
                      {moment(window.kixieBuildEnv?.gitInfo?.date).format(
                        'YYYY-MM-DD hh:mm:ss A'
                      )}
                    </b>
                  </div>
                </div>
              </section>
            </article>
          </>
        )}
        {/* {loginUser.role === 'superadmin' && (
          <>
            <div className="separator separator-dashed mt-1 mb-7"></div>
            <Notes />
          </>
        )} */}

        {/* <div>
          <h5 className="mb-5">Recent Notifications</h5>

          <div className="d-flex align-items-center bg-light-warning rounded p-5 gutter-b">
            <span className="svg-icon svg-icon-warning mr-5">
              <SVG
                src={toAbsoluteUrl('/media/svg/icons/Home/Library.svg')}
                className="svg-icon svg-icon-lg"
              ></SVG>
            </span>

            <div className="d-flex flex-column flex-grow-1 mr-2">
              <a
                href="#"
                className="font-weight-normal text-dark-75 text-hover-primary font-size-lg mb-1"
              >
                Another purpose persuade
              </a>
              <span className="text-muted font-size-sm">Due in 2 Days</span>
            </div>

            <span className="font-weight-bolder text-warning py-1 font-size-lg">
              +28%
            </span>
          </div>

          <div className="d-flex align-items-center bg-light-success rounded p-5 gutter-b">
            <span className="svg-icon svg-icon-success mr-5">
              <SVG
                src={toAbsoluteUrl('/media/svg/icons/Communication/Write.svg')}
                className="svg-icon svg-icon-lg"
              ></SVG>
            </span>
            <div className="d-flex flex-column flex-grow-1 mr-2">
              <a
                href="#"
                className="font-weight-normal text-dark-75 text-hover-primary font-size-lg mb-1"
              >
                Would be to people
              </a>
              <span className="text-muted font-size-sm">Due in 2 Days</span>
            </div>

            <span className="font-weight-bolder text-success py-1 font-size-lg">
              +50%
            </span>
          </div>

          <div className="d-flex align-items-center bg-light-danger rounded p-5 gutter-b">
            <span className="svg-icon svg-icon-danger mr-5">
              <SVG
                src={toAbsoluteUrl(
                  '/media/svg/icons/Communication/Group-chat.svg'
                )}
                className="svg-icon svg-icon-lg"
              ></SVG>
            </span>
            <div className="d-flex flex-column flex-grow-1 mr-2">
              <a
                href="#"
                className="font-weight-normel text-dark-75 text-hover-primary font-size-lg mb-1"
              >
                Purpose would be to persuade
              </a>
              <span className="text-muted font-size-sm">Due in 2 Days</span>
            </div>

            <span className="font-weight-bolder text-danger py-1 font-size-lg">
              -27%
            </span>
          </div>

          <div className="d-flex align-items-center bg-light-info rounded p-5">
            <span className="svg-icon svg-icon-info mr-5">
              <SVG
                src={toAbsoluteUrl('/media/svg/icons/General/Attachment2.svg')}
                className="svg-icon svg-icon-lg"
              ></SVG>
            </span>

            <div className="d-flex flex-column flex-grow-1 mr-2">
              <a
                href="#"
                className="font-weight-normel text-dark-75 text-hover-primary font-size-lg mb-1"
              >
                The best product
              </a>
              <span className="text-muted font-size-sm">Due in 2 Days</span>
            </div>

            <span className="font-weight-bolder text-info py-1 font-size-lg">
              +8%
            </span>
          </div>
        </div> */}
        <SwitchBusinessModal
          open={switchBusinessModal}
          handleSwitchBusinessModal={handleSwitchBusinessModal}
          handleSwitchModal={handleSwitchModal}
        />
      </div>
    </div>
  );
}
